/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import type {
  ReportsFilterInput,
  ReportsFilterByInput,
} from "~/generated/graphql";
import {
  ReportsFilterByFields,
  FilterComparisonOperator,
} from "~/generated/graphql";
import { isEmpty, isNotEmpty, isNotNil } from "~/tools";
import { createFilterByInput } from "~/utils/createFilterByInput";
import { ReportsQuery } from "~/views/resources/types";

const isQueryStateDateGenerated = (queryState: ReportsQuery): boolean => {
  return (
    isNotNil(queryState.dateGeneratedStart) &&
    isNotEmpty(queryState.dateGeneratedStart) &&
    isNotNil(queryState.dateGeneratedEnd) &&
    isNotEmpty(queryState.dateGeneratedEnd)
  );
};

const prepareDateGeneratedFilter = (
  queryState: ReportsQuery
): ReportsFilterByInput[] => {
  if (!isQueryStateDateGenerated(queryState)) {
    return [];
  }

  return [
    {
      field: ReportsFilterByFields.DateGenerated,
      operator: FilterComparisonOperator.Ge,
      values: [queryState.dateGeneratedStart ?? ""],
    },
    {
      field: ReportsFilterByFields.DateGenerated,
      operator: FilterComparisonOperator.Le,
      values: [queryState.dateGeneratedEnd ?? ""],
    },
  ];
};

export const prepareReportsFilterBy = (
  queryState: ReportsQuery
): ReportsFilterByInput[] | undefined => {
  const dateGeneratedFilter = prepareDateGeneratedFilter(queryState);

  const reportStatusesFilter = createFilterByInput<ReportsFilterByFields>(
    ReportsFilterByFields.Status,
    queryState.reportStatuses,
    queryState.excludeReportStatuses
  );

  const reportTypesFilter = createFilterByInput<ReportsFilterByFields>(
    ReportsFilterByFields.ReportType,
    queryState.reportTypes,
    queryState.excludeReportTypes
  );

  const createdByFilter = createFilterByInput<ReportsFilterByFields>(
    ReportsFilterByFields.CreatedBy,
    queryState.createdBy,
    queryState.excludeCreatedBy
  );

  const retVal = [
    ...dateGeneratedFilter,
    ...reportStatusesFilter,
    ...reportTypesFilter,
    ...createdByFilter,
  ];

  return isEmpty(retVal) ? undefined : retVal;
};

export const prepareReportsFilter = (
  queryState: ReportsQuery
): ReportsFilterInput | undefined => {
  const filterBy = prepareReportsFilterBy(queryState);
  const phrase = queryState.searchPhrase?.trim();
  const hasPhrase = phrase && isNotEmpty(phrase);

  if (!hasPhrase && !filterBy) {
    return undefined;
  }

  return {
    phrase: hasPhrase ? phrase : undefined,
    filterBy,
  };
};
